import React, { useState } from 'react';
import moment from 'moment';
import {
  ArrayParam,
  BooleanParam,
  DateParam,
  JsonParam,
  NumberParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import {
  Button,
  FilledInput,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';

import {
  Customer,
  Employee,
  NomenclatureGroup,
  Point,
} from '@/types';
import { useAuth, useDebounce } from '@/hooks';
import {
  AppBar,
  DebouncedCheckbox,
  DebouncedTextField,
  DownloadFile,
  Empty,
  Error,
  Loader,
} from '@/components';
import { SearchPoints } from '@/pages/Points/components';
import { SearchNomenclatureGroups } from '@/pages/NomenclatureGroups/components';
import { SearchCustomers } from '@/pages/Customers/components';
import { SearchEmployees } from '@/pages/Employees/components';
import { PLEDGE_STATUSES } from '@/pages/Pledges';

import {
  ActivePledgesTable,
  ActivePledgesSummaryTable,
} from './components';
import { ChartGrowthFlat } from '@/assets/icons';
import {useLazyGetActivePledgesReportQuery, useLazyGetActivePledgesSummaryReportQuery} from "@/services/reports";

const ActivePledgesPage: React.FC = () => {
  const [
    {
      page,
      with_prolongations,
      with_collateral_items,
      points,
      nomenclature_groups,
      customers,
      employees,
      active_pledges,
      pledge_statuses,
      date_from,
      date_to,
      sum_from,
      sum_to,
    },
    setQuery,
  ] = useQueryParams({
    page: withDefault(NumberParam, 1),
    with_prolongations: withDefault(BooleanParam, false),
    with_collateral_items: withDefault(BooleanParam, false),
    points: withDefault<Partial<Point>[], Point[]>(JsonParam, []),
    nomenclature_groups: withDefault<Partial<NomenclatureGroup>[], NomenclatureGroup[]>(JsonParam, []),
    customers: withDefault<Partial<Customer>[], Customer[]>(JsonParam, []),
    employees: withDefault<Partial<Employee>[], Employee[]>(JsonParam, []),
    active_pledges: withDefault(NumberParam, 1),
    pledge_statuses: withDefault(ArrayParam, []),
    date_from: withDefault(DateParam, null),
    date_to: withDefault(DateParam, moment().toDate()),
    sum_from: withDefault(NumberParam, null),
    sum_to: withDefault(NumberParam, null),
  });

  const { user, isAdmin, isRevisor, isCashier } = useAuth();

  const point_ids = useDebounce(points.map(p => p.id));
  const nomenclature_group_ids = useDebounce(nomenclature_groups.map(n => n.id));
  const customer_ids = useDebounce(customers.map(c => c.id));
  const employee_ids = useDebounce(employees.map(e => e.id));
  const statuses = useDebounce(pledge_statuses);
  const active = useDebounce(active_pledges);

  const params = {
    point_ids: isCashier ? [user?.point?.id] : point_ids,
    nomenclature_group_ids,
    customer_ids,
    employee_ids,
    active,
    statuses,
    date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
    date_to: moment(date_to).format('YYYY-MM-DD'),
    with_prolongations: with_prolongations ? 1 : 0,
    with_collateral_items: with_collateral_items ? 1 : 0,
    sum_from,
    sum_to,
  };

  const emptyFilters =
      (isAdmin || isRevisor) &&
      ![point_ids.length, customer_ids.length, employee_ids.length].some(Boolean);

  // lazy hooks
  const [triggerSummary, { data: summaryData, isLoading: loadingSummary, isFetching: fetchingSummary, isError: errorSummary, isSuccess: successSummary }] =
      useLazyGetActivePledgesSummaryReportQuery();
  const [triggerList, { data: listData, isLoading: loadingList, isFetching: fetchingList, isError: errorList, isSuccess: successList }] =
      useLazyGetActivePledgesReportQuery();

  const [hasSearched, setHasSearched] = useState(false);

  const isLoading = loadingSummary || loadingList;
  const isFetching = fetchingSummary || fetchingList;

  const onSearch = () => {
    if (emptyFilters) return;
    setQuery({ page: 1 });
    setHasSearched(true);
    triggerSummary({ params });
    triggerList({ params: { ...params, page: 1 } });
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    triggerList({ params: { ...params, page: newPage } });
  };

  return (
      <>
        <AppBar title="Активные залоги" />

        <Grid container spacing={3}>
          <Grid item xs={12} className="no-print">
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormGroup sx={{ flexDirection: 'row' }}>
                    <FormControlLabel
                        control={
                          <DebouncedCheckbox
                              checked={with_prolongations}
                              onChange={(_, checked) => setQuery({ with_prolongations: checked })}
                              disabled={isLoading || isFetching}
                          />
                        }
                        label="Вывести сведения о пролонгации"
                    />
                    <FormControlLabel
                        control={
                          <DebouncedCheckbox
                              checked={with_collateral_items}
                              onChange={(_, checked) => setQuery({ with_collateral_items: checked })}
                              disabled={isLoading || isFetching}
                          />
                        }
                        label="Детализация по строкам документа"
                    />
                  </FormGroup>

                  {!emptyFilters && (
                      <DownloadFile url="/admin/reports/active-pledges/excel" fileName="active-pledge.xlsx" params={params}>
                        {(onDownload, state) => (
                            <Button
                                variant="contained"
                                startIcon={<ChartGrowthFlat />}
                                size="large"
                                disabled={isFetching || isLoading || state.isLoading}
                                onClick={onDownload}
                            >
                              Сформировать отчёт
                            </Button>
                        )}
                      </DownloadFile>
                  )}
                </Stack>
              </Grid>

              {(isAdmin || isRevisor) && (
                  <>
                    <Grid item lg={3} md={3} sm={6} xs={12}>
                      <SearchPoints
                          multiple
                          value={points as Point[]}
                          disableCloseOnSelect
                          onChange={(_, opts) => setQuery({ points: opts.map(o => ({ id: o.id, name: o.name })) })}
                          disabled={isLoading}
                          getOptionLabel={o => o.name}
                          renderInput={props => <TextField {...props} name="points" label="Точки" variant="filled" size="small" />}
                      />
                    </Grid>
                    <Grid item lg={3} md={3} sm={6} xs={12}>
                      <SearchEmployees
                          multiple
                          value={employees as Employee[]}
                          limitTags={1}
                          disableCloseOnSelect
                          onChange={(_, opts) => setQuery({ employees: opts.map(o => ({ id: o.id, name: o.name, last_name: o.last_name, father_name: o.father_name })) })}
                          disabled={isLoading}
                          getOptionLabel={e => `${e.last_name} ${e.name} ${e.father_name}`}
                          renderInput={props => <TextField {...props} name="employees" label="Сотрудники" variant="filled" size="small" />}
                      />
                    </Grid>
                  </>
              )}

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <SearchNomenclatureGroups
                    multiple
                    value={nomenclature_groups as NomenclatureGroup[]}
                    limitTags={1}
                    disableCloseOnSelect
                    onChange={(_, opts) => setQuery({ nomenclature_groups: opts.map(o => ({ id: o.id, name: o.name })) })}
                    disabled={isLoading}
                    getOptionLabel={o => o.name}
                    renderInput={props => <TextField {...props} name="nomenclature_groups" label="Номенклатурные группы" variant="filled" size="small" />}
                />
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <SearchCustomers
                    multiple
                    value={customers as Customer[]}
                    limitTags={1}
                    disableCloseOnSelect
                    onChange={(_, opts) => setQuery({ customers: opts.map(o => ({ id: o.id, name: o.name, last_name: o.last_name, father_name: o.father_name })) })}
                    disabled={isLoading}
                    getOptionLabel={c => `${c.last_name ?? ''} ${c.name} ${c.father_name ?? ''}`.trim()}
                    renderInput={props => <TextField {...props} name="customers" label="Клиенты" variant="filled" size="small" />}
                />
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <FormControl fullWidth variant="filled" size="small">
                  <InputLabel>Статусы залогов</InputLabel>
                  <Select multiple value={pledge_statuses} onChange={e => setQuery({ pledge_statuses: e.target.value as string[] })} input={<FilledInput />} disabled={isLoading}>
                    {PLEDGE_STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField select value={active} label="Активные залоги" onChange={e => setQuery({ active_pledges: +e.target.value })} disabled={isLoading} fullWidth variant="filled" size="small">
                  {[{ label: 'Активные', value: 1 }, { label: 'Неактивные', value: 0 }].map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <DatePicker mask="__.__.____" label="Дата от" value={date_from} maxDate={moment(date_to)} onChange={v => setQuery({ date_from: v ? moment(v).toDate() : null })} disabled={isLoading || isFetching} renderInput={params => <TextField {...params} name="date_from" variant="filled" size="small" />} />
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <DatePicker mask="__.__.____" label="Дата до" value={date_to} maxDate={moment()} onChange={v => setQuery({ date_to: v ? moment(v).toDate() : moment().toDate() })} disabled={isLoading || isFetching} renderInput={params => <TextField {...params} name="date_to" variant="filled" size="small" />} />
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField type="number" name="sum_from" label="Сумма от" value={sum_from} onChange={e => setQuery({ sum_from: +e.target.value })} disabled={isLoading} fullWidth variant="filled" size="small" />
              </Grid>

              <Grid item lg={3} md={3} sm={6} xs={12}>
                <TextField type="number" name="sum_to" label="Сумма до" value={sum_to} onChange={e => setQuery({ sum_to: +e.target.value })} disabled={isLoading} fullWidth variant="filled" size="small" />
              </Grid>

              {/* Search button */}
              <Grid item lg={3} md={3} sm={6} xs={12}>
                <LoadingButton loading={isFetching} disabled={isFetching || emptyFilters} onClick={onSearch} variant="contained" size="large" fullWidth>
                  Поиск
                </LoadingButton>
              </Grid>

              {!emptyFilters && (
                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <DownloadFile url="/admin/reports/active-pledges/excel" fileName="active-pledge.xlsx" params={params}>
                      {(onDownload, state) => (
                          <Button variant="contained" startIcon={<ChartGrowthFlat />} size="large" disabled={isFetching || isLoading || state.isLoading} onClick={onDownload} fullWidth>
                            Сформировать отчёт
                          </Button>
                      )}
                    </DownloadFile>
                  </Grid>
              )}
            </Grid>
          </Grid>

          {/* Results */}
          {emptyFilters ? (
              <Grid item xs={12}><Empty text="Выберите фильтр по точкам, сотрудникам или клиентам" /></Grid>
          ) : !hasSearched ? (
              <Grid item xs={12}><Empty text="Нажмите «Поиск», чтобы загрузить данные" /></Grid>
          ) : (
              <>
                <Grid item xs={12}>
                  {loadingSummary ? <Loader /> : errorSummary ? <Error text="Ошибка загрузки сводки!" /> : successSummary && summaryData?.length ? (
                      <ActivePledgesSummaryTable list={summaryData} />
                  ) : <Empty text="Сводные данные отсутствуют" />}
                </Grid>
                <Grid item xs={12}>
                  {loadingList ? <Loader /> : errorList ? <Error text="Ошибка загрузки списка!" /> : successList && listData?.data?.length ? (
                      <ActivePledgesTable list={listData} page={page} onChangePage={onChangePage} />
                  ) : <Empty text="Данные отсутствуют" />}
                </Grid>
              </>
          )}
        </Grid>
      </>
  );
};

export default ActivePledgesPage;
